// import node module libraries
import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Row, Card, Form, Button, Spinner, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// import custom components
import PasswordStrengthMeter from 'components/elements/passwordstrength/PasswordStrengthMeter';

// import authentication components
import useSignIn from 'react-auth-kit/hooks/useSignIn';

// import axios
import axios from 'axios';

// import media files
import Logo from 'assets/images/brand/logo/logo-icon.svg';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const SignIn = () => {
	const { t } = useTranslation();
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const [isLoading, setIsLoading] = useState(false); // State for loading spinner
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [searchParams] = useSearchParams();
	const [password, setPassword] = useState('');
	const action = searchParams.get("action");
	const username = searchParams.get("username");
	const token = searchParams.get("token");
	const redirect_uri = searchParams.get("redirect_uri");
	const notification_type = searchParams.get("notification_type");
	const notification_title = searchParams.get("notification_title");
	const notification_message = searchParams.get("notification_message");

	useEffect(() => {
		if (notification_type && notification_message) {
			handleShowNotification(setNotification, notification_type, t(notification_title), t(notification_message));
		}
	}, [notification_type, notification_message]);

	const oauthSignIn = useSignIn();
	const navigate = useNavigate();

	const [showPassword, setShowPassword] = useState(false);

	const handlePasswordToggle = () => {
		setShowPassword(!showPassword);
	};

	const handleSubmitChangePassword = async (e) => {
		e.preventDefault();
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		// API-Call Options
		const options = {
		};
		const data = {
			username: e.target.username.value,
			oldpassword: e.target.password ? e.target.password.value : '',
			newpassword: e.target.newpassword.value,
			token: e.target.token ? e.target.token.value : ''
		};
		
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/auth/change-password`, data, options)
			.then(response => {
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				handleShowNotification(setNotification, 'success', 'Erfolgreich', response.data.message + '. Du wirst nun zur Anmeldeseite weitergeleitet.');
				// Navigate to SignIn after few seconds
				setTimeout(() => {
					navigate(`/authentication/sign-in?username=${username}`);
				}, 4500); // 4.5 seconds
			})
			.catch((error) => {
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine Zugangsdaten oder kontaktiere den HelpDesk.');
			})
	};

	const handleSubmitLogin = async (e) => {
		e.preventDefault();
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		// API-Call Options
		const options = {
		};
		const data = {
			username: e.target.username.value,
			password: e.target.password.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/auth/login`, data, options)
			.then(response => {
				oauthSignIn({
					auth: {
						token: response.data.token,
						type: 'Bearer',
					},
					userState: response.data.data,
					refresh: response.data.refresh
				});

				if (redirect_uri) {
					// Redirect to the given URL
					navigate(redirect_uri);
					return;
				} else {
					// Redirect to the dashboard
					navigate('/dashboard/overview');
				}
			})
			.catch((error) => {
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				if (error.response?.status === 401 && error.response?.data?.message === 'Please change your password') {
					navigate(`/authentication/sign-in?action=change-password&username=${e.target.username.value}`);
					return;
				}
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				if(errorMessage === 'Network Error.'){
					handleShowNotification(setNotification, 'danger', 'Fehler', 'Netzwerkfehler. Bitte überprüfe deine Internetverbindung oder kontaktiere den HelpDesk.');
					return;
				}
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine Zugangsdaten oder kontaktiere den HelpDesk.');				
			})
	
	};
	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row className="align-items-center justify-content-center g-0 min-vh-100">
				<Col lg={5} md={5} className="py-8 py-xl-0">
					<Card>
						{action === 'change-password' ? (
							<Card.Body className="p-6">
								<div className="mb-4">
									<Link to="/">
										<Image src={Logo} className="mb-4" alt="" />
									</Link>
									<h1 className="mb-1 fw-bold">{t('Change Password')}</h1>
									<span>{t('For security reasons, you must change your password.')}</span>
								</div>
								<Form onSubmit={handleSubmitChangePassword}>
									<Row>
										<Col lg={12} md={12} className="mb-3 d-none">
											{/*  email */}
											<Form.Label>{t('Email')}</Form.Label>
											<Form.Control
												type="text"
												id="username"
												value={username}
												required
											/>
										</Col>
										{token ? (
											<Col lg={12} md={12} className="mb-3 d-none">
												<Form.Group className="mb-3">
													<Form.Label>{t('Token')}</Form.Label>
													<div className="input-group">
														<Form.Control
															type="password"
															id="token"
															placeholder={t('Enter Token')}
															value={token}
															required
														/>
													</div>
												</Form.Group>
											</Col>
										) : (
										<Col lg={12} md={12} className="mb-3">
											<Form.Group className="mb-3">
												<Form.Label>{t('Old Password')}</Form.Label>
												<div className="input-group">
													<Form.Control
														type={showPassword ? 'text' : 'password'}
														id="password"
														placeholder={t('Enter Old Password')}
														autoComplete="password"
														required
													/>
													<span className="input-group-text" onClick={handlePasswordToggle}>
														<i className={`fe ${showPassword ? 'fe-eye-off' : 'fe-eye'}`}></i>
													</span>
												</div>
											</Form.Group>
										</Col>
										)}
										<Col lg={12} md={12} className="mb-3">
											<Form.Group className="mb-3">
												<Form.Label>{t('New Password')}</Form.Label>
												<div className="input-group">
													<Form.Control
														type={showPassword ? 'text' : 'password'}
														id="newpassword"
														placeholder={t('New Password')}
														autoComplete="new-password"
														defaultValue={password}
														onChange={(e) => setPassword(e.target.value)}
														required
													/>
													<span className="input-group-text" onClick={handlePasswordToggle}>
														<i className={`fe ${showPassword ? 'fe-eye-off' : 'fe-eye'}`}></i>
													</span>
												</div>
											</Form.Group>
											<Row className="align-items-center g-0">
												<Col sm={6}>
													<span
														data-bs-toggle="tooltip"
														data-placement="right"
														title=""
													>
														{t('Password strength')}{' '}
														<OverlayTrigger
															key="top"
															placement="top"
															overlay={
																<Tooltip id="tooltip-top">
																	{t('We will help you to set a sufficiently secure password. For the best security, your password should consist of at least 6 characters, a capital letter and a number.')}
																</Tooltip>
															}
														>
															<i className="fas fa-question-circle ms-1"></i>
														</OverlayTrigger>
													</span>
												</Col>
											</Row>
											<PasswordStrengthMeter password={password} />
										</Col>
										<Col lg={12} md={12} className="mb-0 d-grid gap-2">
											<Button variant="primary" type="submit" disabled={isButtonDisabled}>
												{isLoading ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														{t('Change Password')}
													</>
												) : (
													t('Change Password')
												)}
											</Button>
										</Col>
									</Row>
								</Form>
								<hr className="my-4" />
								<div className="mt-4 text-center">
									<span>
										{t('Dont know your old password?')}{' '}
										<Link to="/authentication/forget-password" className="ms-1">
											{t('Forget Password')}
										</Link>
									</span>
								</div>
							</Card.Body>
						) : (
							<Card.Body className="p-6">
								<div className="mb-4">
									<Link to="/">
										<Image src={Logo} className="mb-4" alt="" />
									</Link>
									<h1 className="mb-1 fw-bold">{t('Sign-in')}</h1>
									<span>
										{t('No access yet?')}{' '}
										<Link to="/authentication/sign-up" className="ms-1">
												{t('Start now for free')} <i className="fe fe-arrow-right"></i>	
										</Link>
									</span>
								</div>
								<Form onSubmit={handleSubmitLogin}>
									<Row>
										<Col lg={12} md={12} className="mb-3">
											<Form.Label>{t('Email')}</Form.Label>
											<Form.Control
												type="text"
												id="username"
												placeholder={t('Enter Email')}
												autoComplete="username"
												{...(username ? { value: username } : {})}
												required
											/>
										</Col>
										<Col lg={12} md={12} className="mb-3">
											<Form.Label>{t('Password')} </Form.Label>
											<div className="input-group">
												<Form.Control
													type={showPassword ? 'text' : 'password'}
													id="password"
													placeholder={t('Enter Password')}
													autoComplete="current-password"
													required
												/>
												<span className="input-group-text" onClick={handlePasswordToggle}>
													<i className={`fe ${showPassword ? 'fe-eye-off' : 'fe-eye'}`}></i>
												</span>
											</div>
										</Col>
										<Col lg={12} md={12} className="mb-3">						
											<div className="d-md-flex justify-content-between align-items-center">
												<Link to="/authentication/forget-password">
													{t('Forget Password')}?
												</Link>
											</div>
										</Col>
										<Col lg={12} md={12} className="mb-0 d-grid gap-2">
											<Button variant="primary" type="submit" disabled={isButtonDisabled}>
												{isLoading ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														{t('Sign-in')}
													</>
												) : (
													t('Sign-in')
												)}
											</Button>
										</Col>
									</Row>
								</Form>
								<hr className="my-4" />
								<div className="mt-4 text-center">
									<span>
										{t('Having problems with your access?')}{' '}
											<Link to="/marketing/help-center" className="ms-1" target="_blank">
											{t('Open Help Center')} <i className="fe fe-arrow-up-right"></i>
										</Link>
									</span>
								</div>
							</Card.Body>
						)}
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default SignIn;
